<div class="es">
  <header class="es__hero">
    <div class="es__eyebrow">
      <span class="es__eyebrow-rule" aria-hidden="true"></span>
      Fast geschafft
    </div>
    <h2 class="es__title cr-display">{{ node.title || 'Fast geschafft!' }}</h2>
    <p class="es__lead">Letzter Check: Zusammenfassung, Fotos &amp; Adresse.</p>
  </header>

  @if (isNotfall()) {
    <div class="es__banner es__banner--notfall" role="status">
      <div class="es__banner-icon" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z"/>
        </svg>
      </div>
      <div class="es__banner-text">
        <strong>Deine Anfrage wird als Notfall behandelt.</strong>
        <span>Wir leiten sie an Notdienst-Betriebe in deiner Nähe weiter.</span>
      </div>
    </div>
  }

  <div class="es__cols">
    <!-- ─────────── Linke Spalte: was gewählt wurde (ruhig) ─────────── -->
    <aside class="es__left">
      <!-- Live summary preview -->
      <section class="es__panel">
        <div class="es__panel-head">
          <span class="es__panel-dot" aria-hidden="true"></span>
          <span class="es__panel-label">Dein Auftrag im Überblick</span>
        </div>
        <p class="es__summary">{{ summary() }}</p>
        <p class="es__summary-meta">
          Die endgültige Beschreibung erstellen wir automatisch — du kannst sie nach dem Abschicken einsehen.
        </p>

        <!-- Photos — sekundär, in der Summary-Spalte -->
        <div class="es__panel-divider" aria-hidden="true"></div>
        <div class="es__photos-block" [class.es__photos-block--emphasised]="photoEmphasised()">
          <div class="es__group-head">
            <div class="es__group-title">
              Fotos
              @if (photoEmphasised()) {
                <span class="es__badge es__badge--green">Empfohlen</span>
              } @else {
                <span class="es__group-soft">(optional — hilft beim Einschätzen)</span>
              }
            </div>
            <div class="es__group-counter">{{ photos().length }} / {{ photoCfg.maxFiles }}</div>
          </div>

          <div
            class="es__photos"
            [class.es__photos--dragging]="isDragging"
            (dragover)="onDragOver($event)"
            (dragleave)="onDragLeave($event)"
            (drop)="onDrop($event)">
            @for (p of photos(); track p.clientId) {
              <div class="es__photo">
                <img class="es__photo-img" [src]="p.url" [alt]="''" />
                <button type="button" class="es__photo-remove" (click)="removePhoto(p)" aria-label="Entfernen">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M6 6l12 12M18 6 6 18"/>
                  </svg>
                </button>
              </div>
            }
            @if (photos().length < photoCfg.maxFiles) {
              <button type="button" class="es__photo-add" (click)="triggerFilePicker()" [disabled]="uploading">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M12 16V4M6 10l6-6 6 6M4 20h16"/>
                </svg>
                <span>{{ uploading ? 'Lädt …' : 'Hinzufügen' }}</span>
              </button>
            }
          </div>
          <input #fileInput type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple hidden (change)="onFileInput($event)" />
          <p class="es__group-help">Drag &amp; Drop · JPG, PNG, WebP · max. 10 MB</p>
          @if (uploadError) {
            <p class="es__error" role="alert">{{ uploadError }}</p>
          }
        </div>
      </section>
    </aside>

    <!-- ─────────── Rechte Spalte: was noch zu tun ist ─────────── -->
    <div class="es__right">
      <!-- Örtliche Gegebenheiten (Konzept § 8) -->
      <section class="es__group">
        <div class="es__group-head">
          <div class="es__group-title">Vor-Ort-Hinweise</div>
          <span class="es__group-soft">— damit der Handwerker richtig kalkulieren kann.</span>
        </div>

        <div class="es__field">
          <span class="es__field-label">Stockwerk <span class="es__req">*</span></span>
          <div class="es__opts" role="radiogroup" aria-label="Stockwerk">
            @for (opt of stockwerkOptions; track opt.value) {
              <button type="button" class="es__opt"
                      [class.es__opt--active]="store.oertliche().stockwerk === opt.value"
                      [attr.aria-pressed]="store.oertliche().stockwerk === opt.value"
                      (click)="setStockwerk(opt.value)">
                @if (store.oertliche().stockwerk === opt.value) {
                  <svg class="es__opt-check" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 5 5 9-11"/></svg>
                }
                {{ opt.label }}
              </button>
            }
          </div>
        </div>

        <div class="es__field">
          <span class="es__field-label">Aufzug <span class="es__req">*</span></span>
          <div class="es__opts" role="radiogroup" aria-label="Aufzug">
            @for (opt of aufzugOptions; track opt.value) {
              <button type="button" class="es__opt"
                      [class.es__opt--active]="store.oertliche().aufzugStatus === opt.value"
                      [attr.aria-pressed]="store.oertliche().aufzugStatus === opt.value"
                      (click)="setAufzug(opt.value)">
                @if (store.oertliche().aufzugStatus === opt.value) {
                  <svg class="es__opt-check" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 5 5 9-11"/></svg>
                }
                {{ opt.label }}
              </button>
            }
          </div>
        </div>

        <div class="es__field">
          <span class="es__field-label">Parkmöglichkeit <span class="es__req">*</span></span>
          <div class="es__opts" role="radiogroup" aria-label="Parkmöglichkeit">
            @for (opt of parkenOptions; track opt.value) {
              <button type="button" class="es__opt"
                      [class.es__opt--active]="store.oertliche().parkmoeglichkeit === opt.value"
                      [attr.aria-pressed]="store.oertliche().parkmoeglichkeit === opt.value"
                      (click)="setParken(opt.value)">
                @if (store.oertliche().parkmoeglichkeit === opt.value) {
                  <svg class="es__opt-check" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 5 5 9-11"/></svg>
                }
                {{ opt.label }}
              </button>
            }
          </div>
        </div>

        <div class="es__field">
          <span class="es__field-label">Besonderheiten <span class="es__group-soft">(optional)</span></span>
          <div class="es__opts">
            @for (opt of besonderheitenOptions; track opt.key) {
              <button type="button" class="es__opt"
                      [class.es__opt--active]="isBesonderheitChecked(opt.key)"
                      [attr.aria-pressed]="isBesonderheitChecked(opt.key)"
                      (click)="toggleBesonderheit(opt.key)">
                @if (isBesonderheitChecked(opt.key)) {
                  <svg class="es__opt-check" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 5 5 9-11"/></svg>
                }
                {{ opt.label }}
              </button>
            }
          </div>
        </div>
      </section>

      <!-- Free-text notes -->
      <section class="es__group">
        <div class="es__group-head">
          <div class="es__group-title">
            Weitere Hinweise
            @if (notesEmphasised()) {
              <span class="es__badge es__badge--green">Empfohlen</span>
            } @else {
              <span class="es__group-soft">(optional)</span>
            }
          </div>
          <div class="es__group-counter">{{ store.notes().length }} / {{ notesCfg.maxLength }}</div>
        </div>
        <textarea
          class="es__textarea"
          rows="4"
          [attr.maxlength]="notesCfg.maxLength"
          [placeholder]="notesCfg.placeholder || 'z. B. Hund im Haus, Klingel kaputt, Stockwerk ohne Aufzug …'"
          [value]="store.notes()"
          (input)="onNotesChange($any($event.target).value)">
        </textarea>
      </section>

      <!-- Address -->
      <section class="es__group">
        <div class="es__group-head">
          <div class="es__group-title">Adresse</div>
        </div>
        <form class="es__address" [formGroup]="addressForm" (change)="onAddressChange()" (input)="onAddressChange()">
          <div class="es__row es__row--street">
            <label class="es__field es__field--street">
              <span class="es__field-label">Straße <span class="es__req">*</span></span>
              <app-address-autocomplete
                mode="address"
                [value]="addressForm.controls.strasse.value"
                placeholder="z. B. Friedrich-Ebert-Straße"
                autocompleteAttr="street-address"
                ariaLabel="Straße"
                (valueChange)="onStrasseInput($event)"
                (addressSelected)="onAddressPicked($event)"
              />
            </label>
            <label class="es__field es__field--num">
              <span class="es__field-label">Nr.</span>
              <input type="text" formControlName="hausnummer" placeholder="23a" />
            </label>
          </div>
          <div class="es__row es__row--plz-ort">
            <label class="es__field">
              <span class="es__field-label">PLZ <span class="es__req">*</span></span>
              <input type="text" inputmode="numeric" formControlName="plz" maxlength="5" placeholder="42103" />
            </label>
            <label class="es__field">
              <span class="es__field-label">Ort <span class="es__req">*</span></span>
              <input type="text" formControlName="ort" placeholder="Wuppertal" />
            </label>
          </div>
        </form>
        <div class="es__hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--cr-green-800)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 2 4 5v7c0 5 4 9 8 10 4-1 8-5 8-10V5l-8-3Z"/>
          </svg>
          Deine volle Adresse zeigen wir erst, nachdem du ein Angebot ausgewählt hast.
        </div>
      </section>
    </div>
  </div>

  <div class="es__submit-row">
    @if (missingLabel) {
      <span class="es__submit-reason" aria-live="polite">
        <span class="es__submit-reason-dot" aria-hidden="true"></span>
        Noch: {{ missingLabel }}
      </span>
    }
    <button type="button" class="es__submit" (click)="submit()" [disabled]="!canSubmit">
      {{ node.ctaLabel || 'Auftrag absenden' }}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M5 12h14M13 5l7 7-7 7"/>
      </svg>
    </button>
  </div>
</div>
